import { useMemo, useLayoutEffect, useEffect } from 'react';
import { OrderTypes } from 'common/constants/trading.constants';
import {
  calculateMaxVolume,
  transpondNumToSlider,
} from 'common/utils/calculations.util';
import { reaction } from 'mobx';
import { useVolume } from './useVolume';
import { useLimitOfInvestment } from './useLimitOfInvestment';
import { useAbsoluteLimitOfInvestment } from './useAbsoluteLimitOfInvestment';

export const useNewDealForm = ({
  account,
  store,
  overrideSymbolProperties,
  calculateMaxVolumeDependingOnOpenAtPrice = false,
}) => {
  const freeMargin = useMemo(() => {
    return account.accountInfo.free_margin;
  }, [account.accountInfo.free_margin]);

  const symbolProperties = useMemo(() => {
    const { accountInfo, selectedSymbolPrice, symbol } = account;
    const { leverage } = accountInfo;
    const { rate_to_account_currency: kRate } = selectedSymbolPrice;
    const {
      contract_size: contractSize,
      calc_mode: calcMode,
      volume_min: minVolume,
      volume_max: maxVolume,
      volume_step: volumeStep,
      initial_margin_rate_buy: initialMarginRateBuy,
      initial_margin_rate_sell: initialMarginRateSell,
      digits,
      stops_level: stopsLevel,
      commission_in: commissionIn,
      commission_out: commissionOut,
    } = symbol;
    return {
      kRate,
      contractSize,
      calcMode,
      price: selectedSymbolPrice,
      leverage,
      minVolume: {
        [OrderTypes.Buy]: minVolume,
        [OrderTypes.Sell]: minVolume,
      },
      marginRate: {
        [OrderTypes.Buy]: initialMarginRateBuy,
        [OrderTypes.Sell]: initialMarginRateSell,
      },
      maxVolume,
      volumeStep,
      digits,
      stopsLevel,
      commission: commissionIn + commissionOut,
      ...overrideSymbolProperties,
    };
  }, [
    overrideSymbolProperties,
    account,
    account.accountInfo,
    account.selectedSymbolPrice,
    account.symbol,
  ]);

  const volume = useVolume({
    symbolProperties,
    freeMargin,
    calculateMaxVolumeDependingOnOpenAtPrice,
    store,
  });

  const notEnoughFreeMargin = useMemo(
    () => volume.max <= volume.min,
    [volume.max, volume.min]
  );

  const limitOfInvestment = useLimitOfInvestment({
    volume,
    notEnoughFreeMargin,
    store,
  });

  const absoluteLimitOfInvestment = useAbsoluteLimitOfInvestment({
    limitOfInvestment,
    freeMargin,
    store,
    symbolProperties,
    volume,
  });

  const uiAsk = useMemo(() => {
    return symbolProperties.price.ask.toFixed(symbolProperties.digits);
  }, [symbolProperties.price.ask, symbolProperties.digits]);

  const uiBid = useMemo(() => {
    return symbolProperties.price.bid.toFixed(symbolProperties.digits);
  }, [symbolProperties.price.bid, symbolProperties.digits]);

  const uiRiskMarks = useMemo(() => {
    const transpondedMinLimitOfInvestment = transpondNumToSlider(
      limitOfInvestment.min
    );
    const sliderInterval = 1 - transpondedMinLimitOfInvestment;
    return {
      [transpondedMinLimitOfInvestment + sliderInterval * 0.1]: 'Low',
      [transpondedMinLimitOfInvestment + sliderInterval * 0.25]: 'Medium',
      [transpondedMinLimitOfInvestment + sliderInterval * 0.5]: 'High',
      [transpondedMinLimitOfInvestment + sliderInterval]: 'Very high',
    };
  }, [limitOfInvestment.min]);

  const sliderMinValue = transpondNumToSlider(limitOfInvestment.min);

  const sliderValue = transpondNumToSlider(
    Math.max(limitOfInvestment.value, limitOfInvestment.min)
  );

  const tickSize = useMemo(() => {
    return 1 / 10 ** (symbolProperties.digits || 0);
  }, [symbolProperties.digits]);

  const blockedRangeForDelayedOrders = useMemo(() => {
    const k = 1.5;
    const min =
      symbolProperties.price.bid - symbolProperties.stopsLevel * tickSize * k;
    const max =
      symbolProperties.price.ask + symbolProperties.stopsLevel * tickSize * k;
    return {
      min,
      max,
    };
  }, [
    symbolProperties.price.bid,
    symbolProperties.price.ask,
    symbolProperties.stopsLevel,
    tickSize,
  ]);

  useEffect(() => {
    const disposer = reaction(
      () => {
        return account?.symbol.symbol;
      },
      () => {
        store.clearStore();
      }
    );

    return () => {
      disposer();
    };
  }, [account.symbol.symbol, store]);

  return {
    notEnoughFreeMargin,
    freeMargin,
    symbolProperties,
    tickSize,
    blockedRangeForDelayedOrders,
    params: {
      volume,
      limitOfInvestment,
      absoluteLimitOfInvestment,
    },
    ui: {
      ask: uiAsk,
      bid: uiBid,
      riskMarks: uiRiskMarks,
      sliderValue,
      sliderMinValue,
    },
  };
};
